const ROLE_KEY_PREFIX = "originalRegisteredRole:";

const normalizeEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

const getRoleKey = (email) => {
  const normalizedEmail = normalizeEmail(email);
  return normalizedEmail ? `${ROLE_KEY_PREFIX}${normalizedEmail}` : "";
};

export const storeOriginalRegisteredRole = (email, role) => {
  const key = getRoleKey(email);
  if (!key || !["student", "mentor"].includes(role)) return;

  localStorage.setItem(key, role);
};

export const getOriginalRegisteredRole = (email) => {
  const key = getRoleKey(email);
  if (!key) return null;

  const role = localStorage.getItem(key);
  return ["student", "mentor"].includes(role) ? role : null;
};

const isMentorAccount = (user) =>
  Boolean(user?.isMentor || user?.role === "mentor" || user?.mentor);

/**
 * Resolve the original registration role for a user with a robust fallback
 * chain. This is the single source of truth for determining which dashboard
 * to show after a fresh login.
 *
 * Priority order:
 *  1. user.originalRegisteredRole  (backend field)
 *  2. user.originalRole            (backend alias)
 *  3. localStorage backup          (stored during registration / login)
 *  4. Migration heuristic          (for existing users without the field)
 *  5. Infer from current profile   (last resort — may be inaccurate)
 *
 * After resolving via steps 3-5 the value is persisted to localStorage so
 * subsequent logins are fast and consistent.
 *
 * @param {Object} user - The user object from the login response or Redux state
 * @returns {"student" | "mentor"} The original registration role
 */
export const resolveOriginalRegisteredRole = (user) => {
  if (!user) return "student";

  // 1. Backend field
  if (["student", "mentor"].includes(user.originalRegisteredRole)) {
    return user.originalRegisteredRole;
  }

  // 2. Backend alias
  if (["student", "mentor"].includes(user.originalRole)) {
    return user.originalRole;
  }

  // 3. localStorage backup
  const email = user.email || "";
  const stored = getOriginalRegisteredRole(email);
  if (stored) return stored;

  // 4. Migration heuristic for existing users who registered before
  //    originalRegisteredRole was added.  If the user has both a mentor
  //    profile AND an active student experience they are most likely a
  //    student-turned-mentor.  If they only have a mentor profile they
  //    are most likely an original mentor.
  const hasMentorProfile = Boolean(user?.mentor?.id || user?.experiences?.mentor?.hasProfile);
  const hasStudentExperience = Boolean(user?.experiences?.student?.active);

  if (hasMentorProfile && !hasStudentExperience) {
    // Mentor profile exists but never used student side → original mentor
    const resolved = "mentor";
    storeOriginalRegisteredRole(email, resolved);
    return resolved;
  }

  if (hasMentorProfile && hasStudentExperience) {
    // Both experiences active → most likely started as student, became mentor
    const resolved = "student";
    storeOriginalRegisteredRole(email, resolved);
    return resolved;
  }

  // 5. Last resort: infer from current account flags
  const resolved = isMentorAccount(user) ? "mentor" : "student";
  storeOriginalRegisteredRole(email, resolved);
  return resolved;
};
